import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

function MiniCalendar({requests, holidays}) {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const year = month.getFullYear();
  const mo = month.getMonth();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const startDay = new Date(year, mo, 1).getDay();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const fmt = (d) => `${year}-${String(mo + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const getEvents = (day) => {
    if (!day) return [];
    const ds = fmt(day);
    const evts = requests.filter(r => (r.status === 'approved' || r.status === 'pending') && r.startDate <= ds && r.endDate >= ds);
    const hols = (holidays || []).filter(h => h.date === ds);
    return [...hols.map(h => ({type: 'holiday', label: h.name})), ...evts.map(r => ({type: r.status, label: r.employeeName}))];
  };

  const prev = () => setMonth(new Date(year, mo - 1, 1));
  const next = () => setMonth(new Date(year, mo + 1, 1));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={prev} className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">&larr;</button>
        <h3 className="font-semibold text-gray-900">{month.toLocaleString('default', {month: 'long', year: 'numeric'})}</h3>
        <button onClick={next} className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">&rarr;</button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="bg-gray-50 text-center text-xs font-medium text-gray-500 py-2">{d}</div>
        ))}
        {cells.map((day, i) => {
          const events = getEvents(day);
          const isToday = day && fmt(day) === new Date().toISOString().slice(0, 10);
          return (
            <div key={i} className={`bg-white min-h-[80px] p-1 ${!day ? 'bg-gray-50' : ''}`}>
              {day && (
                <>
                  <span className={`text-xs font-medium ${isToday ? 'bg-indigo-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center' : 'text-gray-700'}`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {events.slice(0, 3).map((e, j) => (
                      <div key={j} className={`text-xs px-1 py-0.5 rounded truncate ${
                        e.type === 'holiday' ? 'bg-purple-100 text-purple-800' :
                        e.type === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{e.label}</div>
                    ))}
                    {events.length > 3 && <div className="text-xs text-gray-400">+{events.length - 3} more</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> Approved</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300" /> Pending</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-300" /> Holiday</span>
      </div>
    </div>
  );
}

export default function PtoManager() {
  const [tab, setTab] = useState('requests');
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [policyForm, setPolicyForm] = useState(null);
  const [requestForm, setRequestForm] = useState(null);
  const [employees, setEmployees] = useState([]);

  const loadPolicies = () => api('/hcm/pto-policies').then(r => r.json()).then(setPolicies).catch(() => {});
  const loadRequests = () => api(`/hcm/pto-requests${filter ? `?status=${filter}` : ''}`).then(r => r.json()).then(setRequests).catch(() => {});
  const loadAllRequests = () => api('/hcm/pto-requests').then(r => r.json()).then(setAllRequests).catch(() => {});

  useEffect(() => {
    loadPolicies();
    loadAllRequests();
    api('/hcm/employees').then(r => r.json()).then(setEmployees).catch(() => {});
    api('/hcm/holidays').then(r => r.ok ? r.json() : []).then(setHolidays).catch(() => {});
  }, []);
  useEffect(() => { loadRequests(); }, [filter]);

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    await api('/hcm/pto-policies', {method: 'POST', body: JSON.stringify(policyForm)});
    setPolicyForm(null);
    loadPolicies();
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    await api('/hcm/pto-requests', {method: 'POST', body: JSON.stringify(requestForm)});
    setRequestForm(null);
    loadRequests();
    loadAllRequests();
  };

  const handleApprove = async (id) => {
    await api(`/hcm/pto-requests/${id}/approve`, {method: 'POST'});
    loadRequests();
    loadAllRequests();
  };

  const handleDeny = async (id) => {
    await api(`/hcm/pto-requests/${id}/deny`, {method: 'POST'});
    loadRequests();
    loadAllRequests();
  };

  const statusBadge = (s) => {
    const cls = s === 'approved' ? 'bg-green-100 text-green-800' : s === 'denied' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800';
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>;
  };

  return (
    <Layout>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <h1 className="text-2xl font-bold text-gray-900">Time Off</h1>
        </div>
        <div className="border-b">
          <nav className="flex gap-6 px-6">
            {[['requests','Requests'],['calendar','Calendar'],['policies','Policies']].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className={`py-3 text-sm font-medium border-b-2 ${tab === key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
            ))}
          </nav>
        </div>

        {tab === 'calendar' && (
          <div className="p-4 md:p-6">
            <MiniCalendar requests={allRequests} holidays={holidays} />
          </div>
        )}

        {tab === 'requests' && (
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex gap-2">
                {['pending','approved','denied',''].map(s => (
                  <button key={s} onClick={() => setFilter(s)}
                    className={`px-3 py-1 rounded-full text-sm ${filter === s ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                    {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
                  </button>
                ))}
              </div>
              <button onClick={() => setRequestForm({employeeId: '', policyId: '', startDate: '', endDate: '', hours: '', notes: ''})}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Request</button>
            </div>
            <div className="divide-y">
              {requests.map(r => (
                <div key={r.id} className="py-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{r.employeeName}</span>
                      {statusBadge(r.status)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{r.policyName} · {r.startDate} to {r.endDate} · {r.hours}h</p>
                    {r.notes && <p className="text-sm text-gray-400 italic mt-1">{r.notes}</p>}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleApprove(r.id)} className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700">Approve</button>
                      <button onClick={() => handleDeny(r.id)} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">Deny</button>
                    </div>
                  )}
                </div>
              ))}
              {requests.length === 0 && <p className="text-gray-500 text-center py-8">No requests found.</p>}
            </div>
          </div>
        )}

        {tab === 'policies' && (
          <div className="p-4 md:p-6">
            <div className="flex justify-end mb-4">
              <button onClick={() => setPolicyForm({name: '', accrualRate: '', accrualPeriod: '', maxBalance: ''})}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Policy</button>
            </div>
            <div className="divide-y">
              {policies.map(p => (
                <div key={p.id} className="py-4 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900">{p.name}</span>
                    <p className="text-sm text-gray-500">
                      {p.accrualRate && `${p.accrualRate}h / ${p.accrualPeriod || 'period'}`}
                      {p.maxBalance && ` · Max: ${p.maxBalance}h`}
                    </p>
                  </div>
                  <button onClick={async () => { await api(`/hcm/pto-policies/${p.id}`, {method: 'DELETE'}); loadPolicies(); }}
                    className="text-sm text-red-600 hover:text-red-800">Remove</button>
                </div>
              ))}
              {policies.length === 0 && <p className="text-gray-500 text-center py-8">No policies defined yet.</p>}
            </div>
          </div>
        )}
      </div>

      {policyForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPolicyForm(null)}>
          <form onSubmit={handleCreatePolicy} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Add PTO Policy</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required value={policyForm.name} onChange={e => setPolicyForm({...policyForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. Vacation" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accrual Rate (hours)</label>
                <input type="number" step="0.01" value={policyForm.accrualRate} onChange={e => setPolicyForm({...policyForm, accrualRate: e.target.value ? parseFloat(e.target.value) : ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Accrual Period</label>
                <select value={policyForm.accrualPeriod} onChange={e => setPolicyForm({...policyForm, accrualPeriod: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none">
                  <option value="">Select...</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Biweekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Balance (hours)</label>
              <input type="number" step="0.01" value={policyForm.maxBalance} onChange={e => setPolicyForm({...policyForm, maxBalance: e.target.value ? parseFloat(e.target.value) : ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setPolicyForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Create</button>
            </div>
          </form>
        </div>
      )}

      {requestForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRequestForm(null)}>
          <form onSubmit={handleCreateRequest} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">New PTO Request</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
              <select required value={requestForm.employeeId} onChange={e => setRequestForm({...requestForm, employeeId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="">Select...</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Policy *</label>
              <select required value={requestForm.policyId} onChange={e => setRequestForm({...requestForm, policyId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none">
                <option value="">Select...</option>
                {policies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input type="date" required value={requestForm.startDate} onChange={e => setRequestForm({...requestForm, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input type="date" required value={requestForm.endDate} onChange={e => setRequestForm({...requestForm, endDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hours *</label>
              <input type="number" step="0.5" required value={requestForm.hours} onChange={e => setRequestForm({...requestForm, hours: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={requestForm.notes} onChange={e => setRequestForm({...requestForm, notes: e.target.value})} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setRequestForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Submit</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
